import { Result, Spin } from "antd";
import { showError } from "common/ToastNotifications";
import AddCommentForm from "components/molecules/AddCommentForm";
import CommentList from "components/molecules/CommentList";
import { Component } from "react";
import { connect } from "react-redux";
import actions from "store/actions";
import { getUsersRequest } from "store/users/actions";

class Comments extends Component {
    constructor(props) {
        super(props);
        this.state = {
            busy: false,
            busyMessage: "",
            listLoading: false,
            creating: false,
        };
    }

    componentDidMount() {
        const { recordId } = this.props;
        this.loadComments(recordId);
        this.props.getUsers();
    }

    componentDidUpdate(prevProps) {
        if (this.props.recordId !== prevProps.recordId) {
            this.loadComments(this.props.recordId);
        }

        this.onGetCommentsResultUpdate(prevProps);
        this.onAddCommentResultUpdate(prevProps);
        this.onUpdateCommentResultUpdate(prevProps);
        this.onDeleteCommentResultUpdate(prevProps);
    }

    loadComments = (recordId, options = {}) => {
        if (!recordId) return;
        const { message = "Loading comments...", listOnly = false } = options;
        this.setState({
            busy: listOnly ? false : true,
            busyMessage: listOnly ? "" : message,
            listLoading: true,
        });
        this.props.getComments(recordId);
    };

    onGetCommentsResultUpdate = (prevProps) => {
        const { getCommentsResult } = this.props;
        if (getCommentsResult !== prevProps.getCommentsResult) {
            this.setState({ busy: false, busyMessage: "", listLoading: false });
            if (getCommentsResult.error) {
                showError("Failed to load comments");
            }
        }
    };

    onAddCommentResultUpdate = (prevProps) => {
        const { addCommentResult, recordId } = this.props;
        if (addCommentResult !== prevProps.addCommentResult) {
            const requestCompleted = addCommentResult?.success || addCommentResult?.error;
            if (requestCompleted) {
                this.setState({ creating: false });
                if (addCommentResult.success) {
                    this.loadComments(recordId, { message: "Refreshing comments..." });
                }
            }
        }
    };

    onUpdateCommentResultUpdate = (prevProps) => {
        const { updateCommentResult, recordId } = this.props;
        if (updateCommentResult !== prevProps.updateCommentResult) {
            if (updateCommentResult.success) {
                this.loadComments(recordId, { message: "Refreshing comments..." });
            }
        }
    };

    onDeleteCommentResultUpdate = (prevProps) => {
        const { deleteCommentResult, recordId } = this.props;
        if (deleteCommentResult !== prevProps.deleteCommentResult) {
            if (deleteCommentResult.success) {
                this.loadComments(recordId, { message: "Refreshing comments..." });
            }
        }
    };

    handleAddComment = (commentData) => {
        const { recordId, recordType } = this.props;
        const payload = {
            ...commentData,
            recordType: recordType,
            redirect_url: typeof window !== "undefined" ? window.location.href : null,
        };
        this.setState({ creating: true });
        this.props.addComment(recordId, payload);
    };

    handleAddReply = (parentCommentId, commentData) => {
        const { recordId, recordType } = this.props;
        const replyData = {
            ...commentData,
            parentCommentId,
            recordType: recordType,
            redirect_url: typeof window !== "undefined" ? window.location.href : null,
        };
        this.props.addComment(recordId, replyData);
    };

    handleUpdateComment = (commentId, commentData) => {
        const { recordId } = this.props;
        const updatePayload = {
            ...commentData,
            redirect_url: typeof window !== "undefined" ? window.location.href : null,
        };
        this.props.updateComment(recordId, commentId, updatePayload);
    };

    handleDeleteComment = (commentId) => {
        const { recordId } = this.props;
        this.props.deleteComment(recordId, commentId);
    };

    render() {
        const { comments, users, getCommentsResult, permission } = this.props;
        const { busy, busyMessage, listLoading, creating } = this.state;
        const listSpinnerActive = listLoading && !busy;

        if (getCommentsResult && getCommentsResult.error) {
            return <Result status="error" title="Error" subTitle="Sorry, something went wrong while loading comments." />;
        }

        return (
            <Spin spinning={busy} tip={busyMessage} style={{ height: "100%" }} classNames={{ wrapper: "full-height-spin" }}>
                <div
                    style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        padding: "24px",
                        background: "#fff",
                    }}
                >
                    <div style={{ flex: "1 1 auto", overflow: "hidden" }}>
                        <CommentList
                            comments={comments}
                            users={users}
                            onUpdate={this.handleUpdateComment}
                            onDelete={this.handleDeleteComment}
                            onAddReply={this.handleAddReply}
                            permission={permission}
                            isLoading={listSpinnerActive}
                        />
                    </div>
                    {permission?.canAdd && (
                        <div
                            style={{
                                paddingTop: "16px",
                                flexShrink: 0,
                                background: "#fff",
                                padding: "0px 12px",
                                margin: "0 -24px -24px",
                            }}
                        >
                            <AddCommentForm users={users} onSubmit={this.handleAddComment} isSubmitting={creating} />
                        </div>
                    )}
                </div>
            </Spin>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const { recordId } = ownProps;
    const commentsForThisRecord = state.comments.commentsByRecordId?.[recordId]?.Items || [];

    return {
        comments: commentsForThisRecord,
        users: state.users?.userList || [],
        getCommentsResult: state.comments.getCommentsResult,
        addCommentResult: state.comments.addCommentResult,
        updateCommentResult: state.comments.updateCommentResult,
        deleteCommentResult: state.comments.deleteCommentResult,
        userCommentsPermission: state.comments.userCommentsPermission,
    };
};

const mapDispatchToProps = (dispatch) => ({
    getComments: (recordId) => dispatch(actions.getCommentsRequest(recordId)),
    addComment: (recordId, commentData) => dispatch(actions.addCommentRequest(recordId, commentData)),
    updateComment: (recordId, commentId, commentData) => dispatch(actions.updateCommentRequest(recordId, commentId, commentData)),
    deleteComment: (recordId, commentId) => dispatch(actions.deleteCommentRequest(recordId, commentId)),
    getUsers: () => dispatch(getUsersRequest()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Comments);
